import DetailsCard from "components/details/card";
import { DetailsContainer } from "components/details/container";
import DetailsItem from "components/details/item";
import { CircleAlert, HelpCircle, Plus, Replace, Trash2 } from "lucide-react";
import { AnimatePresence, motion, type Variants } from "motion/react";
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import settings from "settings";
import {
  type BidPostData,
  getLenderResponseDefaultValues,
} from "validations/bidValidation";

import { ActionMenu } from "@krea/shared/design-system/ActionMenu";
import { Button } from "@krea/shared/design-system/Button";
import { FileInput } from "@krea/shared/design-system/inputs/FileInput";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@krea/shared/design-system/Tabs";
import type { FileWithPreview } from "@krea/shared/hooks/useFileUpload";
import { useIsMobile } from "@krea/shared/hooks/useIsMobile";
import { PromotionTypeEnum } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";
import { useDebounce } from "@krea/shared/utils/use-debounce";

import {
  BidTypeEnum,
  FieldRequestTypeEnum,
  PrimaryBidTypeByProduct,
} from "@/features/common/enums";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { OfferAttractiveness } from "@/features/lender-responses/components/offer-attractiveness";
import { useGetLenderResponsePreview } from "@/features/lender-responses/hooks/data/use-get-lender-response-preview.query";
import type {
  IFieldRequestInput,
  UnionBidInput,
} from "@/features/lender-responses/types/lender-response";
import { capitalizeWithSpaces } from "@/utils";

import { useLenderApplicationDetailsStore } from "../lenderApplicationDetailsStore";
import { BuildingCreditForm } from "./bid-types/building-credit/BuildingCreditForm";
import { LoanForm } from "./bid-types/company-loan/LoanForm";
import { FactoringForm } from "./bid-types/factoring/FactoringForm";
import { LeasingForm } from "./bid-types/leasing/LeasingForm";
import { PurchaseFinancingForm } from "./bid-types/purchase-financing/PurchaseFinancingForm";
import { RealEstateBidForm } from "./bid-types/real-estate/RealEstateBidForm";
import { RevolvingCreditForm } from "./bid-types/revolving-credit/RevolvingCreditForm";
import { useManageLenderResponse } from "./bidHelpers";
import FieldRequestSelector from "./field-requests/FieldRequestSelector";
import { LenderResponse } from "./LenderResponse";
import { PromotionsSelector } from "./PromotionsSelector";
import { SelectedBidIndexContext } from "./SelectedBidIndexContext";

interface BidsFormProps {
  lenderApplication: ILenderApplicationExtended;
  isEditing: boolean;
  isInterestRatePerYearActivated: boolean;
}

export const BidsForm = ({
  lenderApplication,
  isEditing,
  isInterestRatePerYearActivated,
}: BidsFormProps) => {
  const {
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useFormContext<BidPostData>();

  const watchedBids = useWatch({
    control,
    name: "bids",
  });

  // When inside LenderResponseForm we sync tab index to context so OfferAttractiveness can focus the right bid's fields.
  const selectedBidIndexContext = useContext(SelectedBidIndexContext);
  const [internalSelectedTab, setInternalSelectedTab] = useState(0);
  const selectedTab = selectedBidIndexContext
    ? selectedBidIndexContext.selectedBidIndex
    : internalSelectedTab;
  const onSelectedTabChange = selectedBidIndexContext
    ? selectedBidIndexContext.setSelectedBidIndex
    : setInternalSelectedTab;

  const isMobile = useIsMobile();
  const addBid = useCallback(
    (bidType: BidTypeEnum) => {
      const defaultValues = getLenderResponseDefaultValues(
        bidType,
        lenderApplication.loanApplication,
      );
      const initialValue = { ...defaultValues.bids[0] } as UnionBidInput;

      setValue(
        `bids`,
        [...watchedBids, initialValue].map((bid) => ({ ...bid })),
        {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        },
      );

      onSelectedTabChange(watchedBids.length);
    },
    [
      lenderApplication.loanApplication,
      setValue,
      watchedBids,
      onSelectedTabChange,
    ],
  );

  const removeBid = useCallback(
    (index: number) => {
      clearErrors(`bids.${index}`);
      setValue(
        "bids",
        watchedBids.filter((_, i) => i !== index).map((bid) => ({ ...bid })),
        {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        },
      );

      let nextTab = index - 1;

      if (nextTab < 0) {
        nextTab = 0;
      }

      onSelectedTabChange(nextTab);
    },
    [setValue, watchedBids, clearErrors, onSelectedTabChange],
  );

  const replaceBid = useCallback(
    (bidType: BidTypeEnum, index: number) => {
      clearErrors(`bids.${index}`);
      const defaultValues = getLenderResponseDefaultValues(
        bidType,
        lenderApplication.loanApplication,
      );
      const initialData = { ...defaultValues.bids[0] } as UnionBidInput;

      setValue(`bids.${index}`, initialData, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    },
    [lenderApplication.loanApplication, setValue, clearErrors],
  );

  const renderForm = (bidType: BidTypeEnum, index: number): ReactNode => {
    switch (bidType) {
      case BidTypeEnum.COMPANY_LOAN:
        return (
          <LoanForm
            bidFormIndex={index}
            currency={lenderApplication.loanApplication.currency}
            isEditing={isEditing}
            isInterestRatePerYearActivated={isInterestRatePerYearActivated}
          />
        );
      case BidTypeEnum.REAL_ESTATE:
        return (
          <RealEstateBidForm
            bidFormIndex={index}
            currency={lenderApplication.loanApplication.currency}
            isInterestRatePerYearActivated={isInterestRatePerYearActivated}
            isEditing={isEditing}
          />
        );
      case BidTypeEnum.FACTORING:
        return (
          <FactoringForm
            bidFormIndex={index}
            currency={lenderApplication.loanApplication.currency}
            isEditing={isEditing}
          />
        );
      case BidTypeEnum.LEASING:
        return (
          <LeasingForm
            bidFormIndex={index}
            currency={lenderApplication.loanApplication.currency}
            isEditing={isEditing}
          />
        );
      case BidTypeEnum.REVOLVING_CREDIT:
        return (
          <RevolvingCreditForm
            bidFormIndex={index}
            currency={lenderApplication.loanApplication.currency}
            isInterestRatePerYearActivated={isInterestRatePerYearActivated}
            isEditing={isEditing}
          />
        );
      case BidTypeEnum.PURCHASE_FINANCING:
        return (
          <PurchaseFinancingForm
            bidFormIndex={index}
            currency={lenderApplication.loanApplication.currency}
            isInterestRatePerYearActivated={isInterestRatePerYearActivated}
            isEditing={isEditing}
          />
        );
      case BidTypeEnum.BUILDING_CREDIT:
        return (
          <BuildingCreditForm
            bidFormIndex={index}
            currency={lenderApplication.loanApplication.currency}
            isInterestRatePerYearActivated={isInterestRatePerYearActivated}
            isEditing={isEditing}
          />
        );

      default: {
        const exhaustiveCheck: never = bidType;
        console.error(exhaustiveCheck);

        return;
      }
    }
  };

  return (
    <div className="w-full flex-row">
      <DetailsCard className="mb-5">
        <Tabs value={`${selectedTab}`}>
          <TabsList className="mb-4">
            {watchedBids.map((field, index) => (
              <TabsTrigger
                key={field.formKey}
                value={`${index}`}
                onClick={() => onSelectedTabChange(index)}
              >
                <div
                  className={cn("flex items-center gap-1", {
                    "text-red-500":
                      errors.bids?.[index] && selectedTab !== index,
                  })}
                >
                  <span>{capitalizeWithSpaces(field.type)}</span>
                  {errors.bids?.[index] && selectedTab !== index && (
                    <CircleAlert className="size-4" />
                  )}
                </div>
              </TabsTrigger>
            ))}

            <ActionMenu
              items={Object.values(BidTypeEnum).map((item) => ({
                content: capitalizeWithSpaces(item),
                onClick: () => {
                  addBid(item);
                },
              }))}
              trigger={
                // Styled to match an inactive segmented tab (see design-system/Tabs)
                <div className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-transparent px-3 py-1 text-sm font-semibold text-gray-600 transition-all hover:cursor-pointer hover:text-primary">
                  <Plus className="size-4" />
                  <span>Add product</span>
                </div>
              }
            />
          </TabsList>

          {watchedBids.map((field, index) => {
            return (
              <TabsContent
                className="mt-4"
                key={field.formKey}
                value={`${index}`}
              >
                <div className="relative">
                  <div className="absolute right-0 top-0">
                    {watchedBids.length > 1 && (
                      <div className="flex items-center">
                        <ResponsiveTooltip
                          trigger={
                            <Button
                              variant="ghost"
                              tabIndex={0}
                              onKeyUp={(e) => {
                                if (e.key === "Enter") {
                                  removeBid(index);
                                }
                              }}
                              onClick={() => {
                                removeBid(index);
                              }}
                              className=" text-red hover:cursor-pointer"
                            >
                              <div className="relative">
                                <Trash2 className="size-5" />
                              </div>
                            </Button>
                          }
                          desktopOnly
                        >
                          Remove product
                        </ResponsiveTooltip>
                        {isMobile && (
                          <ResponsiveTooltip
                            trigger={
                              <Button variant="ghost" size="icon">
                                <HelpCircle className="size-4" />
                              </Button>
                            }
                          >
                            Remove product
                          </ResponsiveTooltip>
                        )}
                      </div>
                    )}
                    {watchedBids.length === 1 && (
                      <div className="flex items-center">
                        <ActionMenu
                          items={Object.values(BidTypeEnum).map((item) => ({
                            content: capitalizeWithSpaces(item),
                            onClick: () => {
                              replaceBid(item, index);
                            },
                          }))}
                          trigger={
                            <ResponsiveTooltip
                              trigger={
                                <div className="relative rounded-sm p-[3px] text-primary hover:bg-primary hover:text-white">
                                  <Replace />
                                </div>
                              }
                              desktopOnly
                            >
                              Switch product
                            </ResponsiveTooltip>
                          }
                        />
                        {isMobile && (
                          <ResponsiveTooltip
                            trigger={
                              <Button variant="ghost" size="icon">
                                <HelpCircle className="size-4" />
                              </Button>
                            }
                          >
                            Switch product
                          </ResponsiveTooltip>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    {renderForm(field.type, index)}
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </DetailsCard>
    </div>
  );
};
const variants: Variants = {
  initial: {
    opacity: 0,
    x: -100,
  },
  visible: {
    opacity: 1,
    x: 0,
    height: "auto",
    transition: {
      type: "tween",
      duration: 0.2,
      ease: "easeOut",
    },
  },
  hidden: {
    opacity: 0,
    x: -100,
    height: 0,
    transition: {
      type: "tween",
      duration: 0.2,
      ease: "easeOut",
    },
  },
};

interface LenderResponseFormProps {
  lenderApplication: ILenderApplicationExtended;
}

export const LenderResponseForm = ({
  lenderApplication,
}: LenderResponseFormProps) => {
  const { id } = lenderApplication;
  const primaryBidType = PrimaryBidTypeByProduct[lenderApplication.product];

  const {
    control,
    formState: { isDirty, isSubmitting, isValid },
  } = useFormContext<BidPostData>();

  // Single field array instance shared by both FieldRequestSelector renders
  // below (Collateral / Additional Data). Each one calling its own
  // useFieldArray({ name: "fieldRequests" }) corrupts the shared array on
  // remove() (KREA-4311).
  const fieldRequestsArray = useFieldArray({ control, name: "fieldRequests" });

  const fieldRequestsPreview = useWatch({
    control,
    name: "fieldRequests",
  });

  const promotionsPreview = useWatch({
    control,
    name: "promotions",
  });

  const watchedBids = useWatch({
    control,
    name: "bids",
  });

  const debouncedData = useDebounce(
    useMemo(
      () => ({
        bidData: {
          bids: watchedBids,
          fieldRequests: fieldRequestsPreview,
          promotions: promotionsPreview,
        },
        isDirty,
        isValid,
      }),
      [watchedBids, fieldRequestsPreview, promotionsPreview, isDirty, isValid],
    ),
    2000,
  );

  const lenderResponsePreviewFormData = useMemo((): BidPostData => {
    return {
      bids: debouncedData.bidData.bids,
      fieldRequests: debouncedData.bidData.fieldRequests,
      promotions: debouncedData.bidData.promotions,
    };
  }, [debouncedData]);

  const { lenderCompanyId } = useParams<{ lenderCompanyId: string }>();

  const previewData = useGetLenderResponsePreview(
    {
      lenderCompanyId: Number(lenderCompanyId),
      lenderApplicationId: lenderApplication.id,
      input: lenderResponsePreviewFormData,
    },
    {
      enabled:
        lenderResponsePreviewFormData.bids.length > 0 &&
        debouncedData.isValid &&
        debouncedData.isDirty,
    },
  );

  const { handleLenderResponseSubmit, resetLenderResponse } =
    useManageLenderResponse({
      bidType: primaryBidType,
      lenderApplication,
    });

  const creditTermsFileCtrl = useController({
    control,
    name: "creditTermsFile",
  });

  const handleCreditTermsFileChange = useCallback(
    (files: FileWithPreview[]) => {
      creditTermsFileCtrl.field.onChange(files[0]?.file ?? null);
    },
    [creditTermsFileCtrl.field],
  );

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();

  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const showOfferAttractiveness =
    lenderCompany?.isFeatureOfferAttractivenessActivated;

  const { currentlyUpdatingLenderResponseId } =
    useLenderApplicationDetailsStore(
      ({ currentlyUpdatingLenderResponseId }) => ({
        currentlyUpdatingLenderResponseId,
      }),
    );

  const [isEditing, setIsEditing] = useState<boolean>(
    !!currentlyUpdatingLenderResponseId,
  );
  // Shared with BidsForm (writes on tab change) and OfferAttractiveness (reads for focus target).
  const [selectedBidIndex, setSelectedBidIndex] = useState(0);

  useEffect(() => {
    setIsEditing(!!currentlyUpdatingLenderResponseId);
  }, [currentlyUpdatingLenderResponseId]);

  useEffect(() => {
    resetLenderResponse();
    setSelectedBidIndex(0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // depend on the actual ID, not the function — resetLenderResponse recreates on every refetch

  // Keep selectedBidIndex in range when bids are removed
  useEffect(() => {
    const bidCount = watchedBids.length;

    if (bidCount > 0 && selectedBidIndex >= bidCount) {
      setSelectedBidIndex(Math.max(0, bidCount - 1));
    }
  }, [watchedBids.length, selectedBidIndex]);

  const getDefaultFieldRequests = (
    bidType: BidTypeEnum,
  ): IFieldRequestInput[] => {
    let defaultFieldRequests: (IFieldRequestInput | undefined)[] = [];

    switch (bidType) {
      case BidTypeEnum.COMPANY_LOAN:
        defaultFieldRequests = settings.defaultFieldRequestsForLoans;
        break;
      case BidTypeEnum.REAL_ESTATE:
        defaultFieldRequests = settings.defaultFieldRequestsForRealEstate;
        break;
      case BidTypeEnum.FACTORING:
        defaultFieldRequests = settings.defaultFieldRequestsForFactoring;
        break;
      case BidTypeEnum.LEASING:
        defaultFieldRequests = settings.defaultFieldRequestsForLeasing;
        break;
      case BidTypeEnum.REVOLVING_CREDIT:
        defaultFieldRequests = settings.defaultFieldRequestsForRevolvingCredit;
        break;
      case BidTypeEnum.PURCHASE_FINANCING:
        defaultFieldRequests =
          settings.defaultFieldRequestsForPurchaseFinancing;
        break;
      case BidTypeEnum.BUILDING_CREDIT:
        // Never a primary product (BidTypeEnum.BUILDING_CREDIT has no
        // ProductEnum counterpart), so getDefaultFieldRequests is never
        // actually called with it — case exists only for exhaustiveness.
        defaultFieldRequests = [];
        break;

      default: {
        const exhaustiveCheck: never = bidType;
        console.error(exhaustiveCheck);

        return [];
      }
    }

    return defaultFieldRequests.filter(
      (fieldRequest): fieldRequest is IFieldRequestInput =>
        fieldRequest !== undefined,
    );
  };

  const selectedBidIndexValue = useMemo(
    () => ({
      selectedBidIndex,
      setSelectedBidIndex,
    }),
    [selectedBidIndex],
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      {/* Lets BidsForm sync selected tab and OfferAttractiveness focus the current tab's fields. */}
      <SelectedBidIndexContext value={selectedBidIndexValue}>
        <DetailsContainer>
          <div className="flex flex-col items-start gap-5 lg:flex-row">
            <div className="w-full lg:w-1/2">
              <BidsForm
                lenderApplication={lenderApplication}
                isEditing={isEditing}
                isInterestRatePerYearActivated={
                  isInterestRatePerYearActivated ?? false
                }
              />
              <DetailsCard className="mb-5 xl:mb-0">
                <h6 className="text-section">Promotions</h6>
                <div className="mt-4 rounded-sm bg-gray-100 p-5">
                  <PromotionsSelector
                    control={control}
                    supportedPromotionTypes={[
                      PromotionTypeEnum.AMORTISATION_FREE,
                      PromotionTypeEnum.INTEREST_FREE,
                      PromotionTypeEnum.EU_LOAN,
                    ]}
                  />
                </div>
                {!!lenderCompany?.isFeatureCreditTermsFileActivated && (
                  <div className="mt-6 rounded-sm bg-gray-100 p-5">
                    <h6 className="text-section">Upload Termsheet PDF</h6>
                    <div>
                      <FileInput
                        files={
                          creditTermsFileCtrl.field.value
                            ? [creditTermsFileCtrl.field.value]
                            : []
                        }
                        accept=".pdf"
                        multiple={false}
                        onFilesChange={handleCreditTermsFileChange}
                      />
                    </div>
                  </div>
                )}
              </DetailsCard>
            </div>
            <div className="w-full lg:w-1/2">
              <DetailsCard>
                <DetailsItem className="mb-5" title="Collateral / Securities">
                  <FieldRequestSelector
                    control={control}
                    fieldRequestsArray={fieldRequestsArray}
                    currency={lenderApplication.loanApplication.currency}
                    fieldRequests={getDefaultFieldRequests(
                      primaryBidType,
                    ).filter(
                      ({ type }) => type === FieldRequestTypeEnum.COLLATERAL,
                    )}
                  />
                </DetailsItem>
              </DetailsCard>
              <DetailsCard className="mt-5">
                <DetailsItem className="mb-5" title="Additional Data Request">
                  <FieldRequestSelector
                    control={control}
                    fieldRequestsArray={fieldRequestsArray}
                    currency={lenderApplication.loanApplication.currency}
                    fieldRequests={getDefaultFieldRequests(
                      primaryBidType,
                    ).filter(
                      ({ type }) =>
                        type === FieldRequestTypeEnum.ADDITIONAL_INFORMATION,
                    )}
                  />
                </DetailsItem>
              </DetailsCard>

              {lenderCompany && (
                <DetailsCard className="mt-5">
                  <DetailsItem className="mb-5">
                    <OfferAttractiveness
                      lenderApplicationId={id}
                      isFeatureDisabled={!showOfferAttractiveness}
                    />
                  </DetailsItem>
                </DetailsCard>
              )}
            </div>
          </div>
        </DetailsContainer>
        <AnimatePresence initial={false}>
          {isDirty && (
            <motion.section
              className="mb-7"
              initial="initial"
              exit="hidden"
              animate="visible"
              variants={variants}
            >
              <div>
                <h1 className="mb-5 text-section">Offer preview</h1>
                <LenderResponse
                  lenderResponse={{
                    // A legacy null `earlyRepayment.type` passes through
                    // unchanged — never defaulted to FREE, which would
                    // misrepresent an unclassified bid as free of charge
                    // (KREA-4218 Finding 1).
                    bids: previewData.data?.bids ?? [],
                    promotions: previewData.data?.promotions ?? [],
                    fieldRequests: previewData.data?.fieldRequests ?? [],
                    benefits: previewData.data?.benefits ?? [],
                  }}
                  lenderApplication={lenderApplication}
                  preview={true}
                  isPreviewLoading={previewData.isFetching}
                />
              </div>
            </motion.section>
          )}
        </AnimatePresence>
        <div className="flex justify-center xl:justify-start">
          <Button
            size="lg"
            className="text-base"
            disabled={isSubmitting}
            onClick={() => {
              handleLenderResponseSubmit();
            }}
          >
            {isEditing ? "Update offer" : "Place offer"}
          </Button>
          {isEditing && (
            <Button
              size="lg"
              variant="danger"
              className="ml-2 text-base"
              onClick={() => resetLenderResponse()}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
        </div>
      </SelectedBidIndexContext>
    </form>
  );
};
